import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { INFO } from "@/lib/data";
import { scrollToId } from "./SmoothScroll";

const LINKS = [
  { id: "menu", label: "Menu" },
  { id: "galleria", label: "Galleria" },
  { id: "chi-siamo", label: "Chi siamo" },
  { id: "recensioni", label: "Recensioni" },
  { id: "contatti", label: "Contatti" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else {
      scrollToId(id);
    }
  };

  const dark = scrolled || open;

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          dark ? "bg-bone/90 backdrop-blur-xl border-b border-charcoal/10" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <button
            onClick={() => go("top")}
            data-testid="nav-logo"
            className="flex flex-col items-start leading-none"
          >
            <span
              className={`font-serif text-2xl sm:text-[27px] tracking-tight transition-colors duration-300 ${
                dark ? "text-charcoal" : "text-bone text-shadow-soft"
              }`}
            >
              Gabri
            </span>
            <span
              className={`text-[9px] tracking-[0.34em] uppercase font-sans transition-colors duration-300 ${
                dark ? "text-terracotta" : "text-bone/85"
              }`}
            >
              Bar Ristorante
            </span>
          </button>

          <div className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-${l.id}`}
                className={`text-[13px] font-sans font-medium tracking-wide relative group transition-colors duration-300 ${
                  dark ? "text-charcoal/80 hover:text-terracotta" : "text-bone/90 hover:text-white"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${INFO.phoneTel}`}
              data-testid="nav-call-btn"
              className={`flex items-center gap-2 text-[13px] font-medium transition-colors duration-300 ${
                dark ? "text-charcoal hover:text-terracotta" : "text-bone hover:text-white"
              }`}
            >
              <Phone size={15} strokeWidth={2} />
              {INFO.phoneDisplay}
            </a>
            <button
              onClick={() => go("prenota")}
              data-testid="nav-prenota-btn"
              className="bg-terracotta hover:bg-terracotta-dark text-bone px-5 py-2.5 rounded-sm text-[13px] font-semibold tracking-wide transition-colors duration-300"
            >
              Prenota ora
            </button>
          </div>

          <button
            className={`md:hidden p-1 transition-colors ${dark ? "text-charcoal" : "text-bone"}`}
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
            aria-expanded={open}
          >
            {open ? <X size={26} /> : <MenuIcon size={26} />}
          </button>
        </nav>
      </header>

      {open && (
        <div
          className="fixed top-[68px] inset-x-0 z-40 bg-bone border-b border-charcoal/10 md:hidden"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="text-left py-3 font-serif text-2xl text-charcoal border-b border-charcoal/5"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("prenota")}
              className="mt-4 bg-terracotta text-bone py-3.5 rounded-sm font-semibold tracking-wide"
            >
              Prenota ora
            </button>
          </div>
        </div>
      )}
    </>
  );
}
